import React, { useState, useEffect, useCallback } from 'react';
import { makeStyles, Spinner, Text, tokens } from '@fluentui/react-components';
import { ContentCard } from '../../../components/layout';
import { ApiClient, newToast } from '../../../configurations/index.config';
import { getErrorMessage } from '../../../types';
import {
  SiteClockStatusResponse,
  DashboardFilters,
  FilterOptions,
  SiteClockStatus,
  SiteStatus,
  DEFAULT_FILTERS,
  REFRESH_INTERVAL_MS,
  isSiteClockStatusResponse,
  isApiErrorResponse,
  SiteMonitoringDashboardProps,
  createMockResponse,
} from './types';
import { FilterBar } from './components/FilterBar';
import { SiteStatusGrid } from './components/SiteStatusGrid';
import { SiteDetailsModal } from './components/SiteDetailsModal';
import { StatusLegend } from './components/StatusLegend';
import { RefreshControl } from './components/RefreshControl';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '16px',
    marginBottom: '8px',
  },
  titleSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  subtitle: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground3,
  },
  statsRow: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  statBadge: {
    display: 'flex',
    alignItems: 'center',
  },
  controlsRow: {
    display: 'flex',
    gap: '16px',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '80px 20px',
    flexDirection: 'column',
    gap: '16px',
  },
  errorContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '80px 20px',
    flexDirection: 'column',
    gap: '16px',
    color: tokens.colorPaletteRedForeground1,
  },
});

export const SiteMonitoringDashboard: React.FC = () => {
  const styles = useStyles();

  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [filters, setFilters] = useState<DashboardFilters>(DEFAULT_FILTERS);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    zones: [],
    clients: [],
    shifts: [],
  });
  const [data, setData] = useState<SiteClockStatusResponse | null>(null);
  const [selectedSite, setSelectedSite] = useState<SiteClockStatus | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<SiteStatus | 'all'>('all');
  const itemsPerPage = 50;

  const fetchData = useCallback(
    async (showLoading = true) => {
      if (showLoading) {
        setLoading(true);
      } else {
        setIsRefreshing(true);
      }

      try {
        const params = new URLSearchParams();
        params.append('date', filters.date);
        params.append('page', currentPage.toString());
        params.append('limit', itemsPerPage.toString());

        if (filters.zone_id) {
          params.append('zone_id', filters.zone_id);
        }
        if (filters.client_id) {
          params.append('client_id', filters.client_id);
        }
        if (filters.shift_id) {
          params.append('shift_id', filters.shift_id);
        }
        if (filters.show_unassigned) {
          params.append('show_unassigned', 'true');
        }

        const response = await ApiClient.get(
          `/api/v1/site-monitoring/clock-status?${params.toString()}`
        );

        if (isSiteClockStatusResponse(response.data)) {
          setData(response.data);
          setLastUpdated(new Date().toISOString());
        } else if (isApiErrorResponse(response.data)) {
          newToast('error', response.data.error.message);
          setData(null);
        } else {
          console.warn('API response validation failed, using mock data for development');
          console.log('Received response:', response.data);
          const mockData = createMockResponse(20);
          setData(mockData);
          setLastUpdated(new Date().toISOString());
          newToast('info', 'Using mock data - Backend API not yet implemented');
        }
      } catch (error) {
        console.warn('API call failed, using mock data for development:', error);
        const mockData = createMockResponse(20);
        setData(mockData);
        setLastUpdated(new Date().toISOString());
        newToast('info', 'Using mock data - Backend API not yet implemented');
      } finally {
        setLoading(false);
        setIsRefreshing(false);
      }
    },
    [filters, currentPage, itemsPerPage]
  );

  const fetchFilterOptions = useCallback(async () => {
    try {
      const [zonesRes, clientsRes, shiftsRes] = await Promise.all([
        ApiClient.get('/get-zones'),
        ApiClient.get('/get-clients'),
        ApiClient.get('/get-shifts'),
      ]);

      setFilterOptions({
        zones:
          zonesRes.data?.data?.map((z: any) => ({
            id: z.id,
            label: z.name || z.zone_name,
          })) || [],
        clients:
          clientsRes.data?.data?.map((c: any) => ({
            id: c.id,
            label: c.client_name,
          })) || [],
        shifts:
          shiftsRes.data?.data?.map((s: any) => ({
            id: s.id,
            label: s.name,
          })) || [],
      });
    } catch (error) {
      newToast('error', `Failed to load filter options: ${getErrorMessage(error)}`);
    }
  }, []);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  useEffect(() => {
    fetchData(true);
  }, [fetchData]);

  useEffect(() => {
    if (!autoRefresh) {
      return;
    }

    const interval = setInterval(() => {
      fetchData(false);
    }, REFRESH_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [autoRefresh, fetchData]);

  const handleFilterChange = (newFilters: DashboardFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleManualRefresh = () => {
    fetchData(false);
  };

  const handleSiteClick = (site: SiteClockStatus) => {
    setSelectedSite(site);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedSite(null);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleStatusFilter = (status: SiteStatus | 'all') => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  // Filter sites based on selected status
  const filteredSites =
    statusFilter === 'all'
      ? data?.data || []
      : (data?.data || []).filter(site => site.status === statusFilter);

  if (loading && !data) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.titleSection}>
            <Text className={styles.title}>Site Clock-In Monitoring</Text>
            <Text className={styles.subtitle}>Real-time guard clock-in compliance tracking</Text>
          </div>
        </div>
        <ContentCard>
          <div className={styles.loadingContainer}>
            <Spinner size="large" label="Loading site monitoring data..." />
          </div>
        </ContentCard>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <Text className={styles.title}>Site Clock-In Monitoring</Text>
          <Text className={styles.subtitle}>
            {data?.summary.total_sites || 0} sites • Last updated:{' '}
            {lastUpdated ? new Date(lastUpdated).toLocaleTimeString() : 'Never'}
          </Text>
        </div>

        <div className={styles.controlsRow}>
          <RefreshControl
            autoRefresh={autoRefresh}
            onToggleAutoRefresh={() => setAutoRefresh(!autoRefresh)}
            lastUpdated={lastUpdated}
            onManualRefresh={handleManualRefresh}
            isRefreshing={isRefreshing}
          />
        </div>
      </div>

      <FilterBar
        filters={filters}
        onFilterChange={handleFilterChange}
        zones={filterOptions.zones}
        clients={filterOptions.clients}
        shifts={filterOptions.shifts}
        loading={loading}
      />

      {data?.summary && (
        <StatusLegend
          summary={data.summary}
          activeFilter={statusFilter}
          onStatusFilter={handleStatusFilter}
        />
      )}

      <ContentCard noPadding>
        <SiteStatusGrid
          sites={filteredSites}
          loading={loading}
          onSiteClick={handleSiteClick}
          currentPage={currentPage}
          totalPages={Math.ceil(filteredSites.length / itemsPerPage) || 1}
          onPageChange={handlePageChange}
        />
      </ContentCard>

      <SiteDetailsModal site={selectedSite} open={modalOpen} onClose={handleModalClose} />
    </div>
  );
};

export default SiteMonitoringDashboard;
